/**
 * ComponenteCabecalho - Header da Aplicação
 * 
 * CONTEXTO DE NEGÓCIO:
 * Componente de cabeçalho principal da plataforma jurídica.
 * Exibe o título/logo da aplicação e navegação principal.
 * 
 * FUNCIONALIDADES:
 * - Exibir nome/logo da plataforma
 * - Navegação entre páginas principais
 * - Indicador visual da página ativa
 * 
 * RESPONSABILIDADES:
 * - Renderizar cabeçalho consistente em todas as páginas
 * - Gerenciar navegação via React Router
 * - Fornecer feedback visual de localização do usuário
 */

import React, { useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Scale, FileSearch, FilePlus, Building2, ChevronDown, LogOut } from 'lucide-react';
import { useAutenticacao } from '../../contextos/ContextoAutenticacao';
import { useOrganizacao } from '../../contextos/ContextoOrganizacao';

/**
 * Componente de cabeçalho principal da aplicação
 * 
 * IMPLEMENTAÇÃO:
 * - Usa React Router para navegação
 * - useLocation para determinar página ativa
 * - Lucide React para ícones
 * - TailwindCSS para estilização
 */
export function ComponenteCabecalho(): JSX.Element {
  // Hook para obter localização atual (rota ativa)
  const localizacao = useLocation();
  const navigate = useNavigate();

  // Autenticação: obter usuário e ações do contexto
  const { usuario, logout, estaAutenticado } = useAutenticacao();
  
  // Organização: obter organização selecionada
  const { organizacaoAtual } = useOrganizacao();

  // Estado do dropdown de perfil
  const [dropdownAberto, setDropdownAberto] = useState<boolean>(false);
  const refDropdown = useRef<HTMLDivElement | null>(null);

  // Fecha o dropdown ao clicar fora ou ao pressionar Escape
  useEffect(() => {
    function handleClickFora(event: MouseEvent) {
      if (refDropdown.current && !refDropdown.current.contains(event.target as Node)) {
        setDropdownAberto(false);
      }
    }

    function handleEsc(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        setDropdownAberto(false);
      }
    }

    document.addEventListener('mousedown', handleClickFora);
    document.addEventListener('keydown', handleEsc);
    return () => {
      document.removeEventListener('mousedown', handleClickFora);
      document.removeEventListener('keydown', handleEsc);
    };
  }, []);

  /**
   * Extrai até duas iniciais de um nome completo.
   * Ex: "João Silva" -> "JS"
   */
  const obterIniciais = (nome: string | undefined | null): string => {
    if (!nome) return '';
    try {
      return nome
        .split(' ')
        .filter(Boolean)
        .map((p) => p[0])
        .slice(0, 2)
        .join('')
        .toUpperCase();
    } catch {
      return '';
    }
  };

  const handleToggleDropdown = () => setDropdownAberto((s) => !s);

  const handleLogout = () => {
    // Fecha dropdown, limpa sessão e redireciona para /login
    setDropdownAberto(false);
    try {
      logout();
    } catch (e) {
      // Não interromper a navegação se logout falhar localmente
      console.warn('Falha ao executar logout', e);
    }
    navigate('/login');
  };

  /**
   * Função auxiliar para determinar se um link está ativo
   * 
   * @param caminho - Caminho da rota a verificar
   * @returns true se a rota atual corresponde ao caminho
   */
  const estaAtivo = (caminho: string): boolean => {
    return localizacao.pathname === caminho;
  };

  /**
   * Função auxiliar para gerar classes CSS de um link de navegação
   * 
   * @param caminho - Caminho da rota
   * @returns String de classes CSS do TailwindCSS
   */
  const obterClassesLink = (caminho: string): string => {
    const classesBase = 'flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-colors duration-200';
    const classesAtivo = 'bg-primary-100 text-primary-700';
    const classesInativo = 'text-gray-600 hover:bg-gray-100 hover:text-gray-900';
    
    return `${classesBase} ${estaAtivo(caminho) ? classesAtivo : classesInativo}`;
  };

  return (
    <header className="border-b border-gray-200 bg-white shadow-sm">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="flex h-16 items-center justify-between">
          {/* Logo e Título da Aplicação */}
          <Link to="/" className="flex items-center gap-3 transition-opacity hover:opacity-80">
            <Scale className="size-8 text-primary-600" />
            <div>
              <h1 className="text-xl font-bold text-gray-900">
                Workforce juridico
              </h1>
              <p className="text-xs text-gray-500">Análise Inteligente de Documentos</p>
            </div>
          </Link>

          {/* Navegação Principal e Perfil */}
          <nav className="flex items-center gap-4">
            <Link to="/analise-peticao" className={obterClassesLink('/analise-peticao')}>
              <FileSearch className="size-5" />
              <span>Análise</span>
            </Link>

            <Link to="/dashboard" className={obterClassesLink('/dashboard')}>
              <FileSearch className="size-5" />
              <span>Processos</span>
            </Link>
            <Link to="/criacao-peticao-inicial" className={obterClassesLink('/criacao-peticao-inicial')}>
              <FilePlus className="size-5" />
              <span>Criar Petição</span>
            </Link>
            
            {/* Link para Organização (se autenticado) */}
            {estaAutenticado && (
              <Link to="/organizacao" className={obterClassesLink('/organizacao')}>
                <Building2 className="size-5" />
                <span className="hidden lg:inline">
                  {organizacaoAtual ? organizacaoAtual.nome : 'Escritório'}
                </span>
              </Link>
            )}
            
            {/* Espaço para o perfil do usuário */}
            <div className="flex items-center">
              {estaAutenticado && usuario ? (
                <div className="relative" ref={refDropdown}>
                  <button
                    aria-expanded={dropdownAberto}
                    aria-label={dropdownAberto ? 'Fechar menu de perfil' : 'Abrir menu de perfil'}
                    onClick={handleToggleDropdown}
                    className="flex items-center gap-2 rounded-md px-3 py-1 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-primary-500"
                  >
                    <div className="flex size-8 items-center justify-center rounded-full bg-primary-600 font-semibold text-white">
                      {obterIniciais(usuario.nome)}
                    </div>
                    <span className="hidden text-sm font-medium text-gray-800 sm:inline">{usuario.nome}</span>
                    <ChevronDown className="size-4 text-gray-600" />
                  </button>

                  {dropdownAberto && (
                    <div className="absolute right-0 z-50 mt-2 w-64 rounded-lg bg-white shadow-lg ring-1 ring-black/5 transition-all duration-150">
                      <div className="border-b p-4">
                        <p className="font-medium text-gray-900">{usuario.nome}</p>
                        <p className="break-words text-sm text-gray-600">{usuario.email}</p>
                      </div>
                      <button
                        onClick={handleLogout}
                        className="flex w-full items-center gap-2 p-3 text-left text-gray-700 hover:bg-gray-50"
                      >
                        <LogOut className="mr-2 inline size-4" />
                        Sair
                      </button>
                    </div>
                  )}
                </div>
              ) : (
                <Link to="/login" className="btn-primary">
                  Entrar
                </Link>
              )}
            </div>
          </nav>
        </div>
      </div>
    </header>
  );
}
